import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Copy, ArrowUpDown } from 'lucide-react';

// ─── Customer Suggestion (V1.4B) ─────────────────────────────────────
export interface CustomerSuggestion {
    customerId: string;
    name: string;
    confidence: number;
    reason: string;
}

// ─── Draft PO Interface ──────────────────────────────────────────────
export interface DraftPO {
    _rowIndex: number;
    _confidence: number;
    _fieldConfidence: Record<string, number>;
    _warnings: string[];
    _isDuplicate: boolean;
    _existingPoId?: string;
    _source: 'csv' | 'excel' | 'text' | 'pdf';
    _customerSuggestions?: CustomerSuggestion[]; // V1.4B suggestions
    _originalClientName?: string; // V1.4C
    _saveAsAlias?: boolean; // V1.4C
    _isVisualFallback?: boolean; // V1.5
    poNumber: string | null;
    clientName: string | null;
    customerId?: string | null;
    type: 'inbound' | 'outbound';
    dueDate?: string | null;
    priority?: 'standard' | 'urgent' | 'critical';
    totalSkids?: number | null;
    totalWeight?: number | null;
    description?: string | null;
    referenceNumber?: string | null;
    notes?: string | null;
    requiredTemperature?: string | null;
    handlingType?: string | null;
}

// ─── Props ───────────────────────────────────────────────────────────
interface POImportReviewTableProps {
    drafts: DraftPO[];
    onDraftsChange: (drafts: DraftPO[]) => void;
    selectedRows: Set<number>;
    onSelectionChange: (selected: Set<number>) => void;
}

// ─── Inline Editable Cell ────────────────────────────────────────────
interface EditableCellProps {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
    isRequired?: boolean;
    isMissing?: boolean;
    confidence?: number;
    mono?: boolean;
    type?: 'text' | 'number' | 'date';
}

function EditableCell({
    value,
    onChange,
    placeholder = '',
    isRequired = false,
    isMissing = false,
    confidence,
    mono = false,
    type = 'text',
}: EditableCellProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { setDraft(value); }, [value]);
    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const commit = useCallback(() => {
        setEditing(false);
        if (draft !== value) onChange(draft);
    }, [draft, value, onChange]);

    const cancel = useCallback(() => {
        setEditing(false);
        setDraft(value);
    }, [value]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    }, [commit, cancel]);

    const hasConfidence = confidence !== undefined;
    const isHigh = hasConfidence && confidence >= 0.85;
    const isMedium = hasConfidence && confidence >= 0.60 && confidence < 0.85;
    const isLow = hasConfidence && confidence < 0.60;

    const cellClasses = [
        'px-2 py-1 text-sm cursor-text rounded transition-all min-h-[32px] flex items-center relative',
        mono ? 'font-mono' : '',
        !isMissing && isLow ? 'border-l-2 border-l-amber-400' : '',
        !isMissing && isMedium ? 'border-l-2 border-l-yellow-400' : '',
        !isMissing && isHigh ? 'border-l-2 border-l-emerald-400' : '',
        isMissing ? 'bg-red-50 border-l-2 border-l-red-500' : '',
    ].filter(Boolean).join(' ');

    if (editing) {
        return (
            <input
                ref={inputRef}
                type={type}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={handleKeyDown}
                placeholder={isRequired && isMissing ? 'Required' : placeholder}
                className={`w-full px-2 py-1 text-sm bg-white ring-2 ring-blue-500 rounded outline-none ${mono ? 'font-mono' : ''}`}
                step={type === 'number' ? 'any' : undefined}
            />
        );
    }

    return (
        <div
            onClick={() => setEditing(true)}
            className={cellClasses}
            title={isLow ? 'Low confidence – verify this field' : isMedium ? 'Medium confidence' : undefined}
        >
            {value || (
                <span className={`${isMissing ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                    {isRequired && isMissing ? 'Required' : placeholder || '—'}
                </span>
            )}
            {hasConfidence && value && !isMissing && (
                <div className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${isHigh ? 'bg-emerald-400' : isMedium ? 'bg-yellow-400' : 'bg-amber-400'}`} title={`${Math.round(confidence * 100)}% confidence`} />
            )}
        </div>
    );
}

// ─── Dropdown Cell ───────────────────────────────────────────────────
interface DropdownCellProps {
    value: string;
    options: { value: string; label: string }[];
    onChange: (val: string) => void;
    confidence?: number;
}

function DropdownCell({ value, options, onChange, confidence }: DropdownCellProps) {
    const hasConfidence = confidence !== undefined;
    const isHigh = hasConfidence && confidence >= 0.85;
    const isMedium = hasConfidence && confidence >= 0.60 && confidence < 0.85;
    const isLow = hasConfidence && confidence < 0.60;

    return (
        <select
            value={value}
            onChange={e => onChange(e.target.value)}
            className={`text-sm bg-transparent border border-slate-200 rounded px-1.5 py-1 outline-none cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-blue-300 transition-all ${
                isLow ? 'border-l-2 border-l-amber-400' : isMedium ? 'border-l-2 border-l-yellow-400' : isHigh ? 'border-l-2 border-l-emerald-400' : ''
            }`}
        >
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    );
}

// ─── Confidence Dot ──────────────────────────────────────────────────
function ConfidenceDot({ confidence }: { confidence: number }) {
    const color = confidence >= 0.8 ? '#22c55e' : confidence >= 0.5 ? '#f59e0b' : '#ef4444';
    const label = confidence >= 0.8 ? 'High' : confidence >= 0.5 ? 'Medium' : 'Low';
    return (
        <div className="flex items-center gap-1.5" title={`${Math.round(confidence * 100)}% confidence`}>
            <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: color }}
            />
            <span className="text-xs text-slate-500 tabular-nums">{Math.round(confidence * 100)}%</span>
            <span className="sr-only">{label} confidence</span>
        </div>
    );
}

// ─── Row Status & Badges ─────────────────────────────────────────────
export type RowStatus = 'READY' | 'NEEDS_PO' | 'NEEDS_CUSTOMER' | 'DUPLICATE' | 'LOW_CONFIDENCE' | 'MISSING_REQUIRED';

export function getRowStatus(draft: DraftPO): RowStatus {
    if (draft._isDuplicate) return 'DUPLICATE';
    if (!draft.poNumber) return 'NEEDS_PO';
    if (!draft.customerId && !draft.clientName) return 'MISSING_REQUIRED';
    if (!draft.customerId) return 'NEEDS_CUSTOMER'; // Has name but unresolved
    
    // Check low confidence on populated critical fields (exclude missing)
    const criticalFields = ['poNumber', 'clientName', 'dueDate', 'totalSkids', 'totalWeight'] as const;
    const hasLowConfidence = criticalFields.some(field => {
        const val = draft[field];
        const conf = draft._fieldConfidence[field];
        return val != null && conf !== undefined && conf < 0.60;
    });

    if (hasLowConfidence) return 'LOW_CONFIDENCE';

    return 'READY';
}

function StatusBadges({ draft }: { draft: DraftPO }) {
    const status = getRowStatus(draft);

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5 flex-wrap">
                {status === 'DUPLICATE' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">
                        <Copy size={10} />
                        Duplicate
                    </span>
                )}
                {status === 'NEEDS_PO' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
                        Needs PO Number
                    </span>
                )}
                {status === 'NEEDS_CUSTOMER' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">
                        <CheckCircle2 size={10} />
                        Ready (New Client)
                    </span>
                )}
                {status === 'MISSING_REQUIRED' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
                        Missing Required
                    </span>
                )}
                {status === 'LOW_CONFIDENCE' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                        <AlertTriangle size={10} />
                        Low Confidence
                    </span>
                )}
                {status === 'READY' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                        <CheckCircle2 size={10} />
                        Ready
                    </span>
                )}

                {draft._isVisualFallback && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded ml-auto">
                        Visual AI
                    </span>
                )}
            </div>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════════
// ─── Main Component ─────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════

export function POImportReviewTable({
    drafts,
    onDraftsChange,
    selectedRows,
    onSelectionChange,
}: POImportReviewTableProps) {

    // ─── Filter State ─────────────────────────────────────────────
    type FilterState = 'ALL' | 'READY' | 'NEEDS_REVIEW' | 'MISSING_REQUIRED' | 'DUPLICATES';
    const [filter, setFilter] = useState<FilterState>('ALL');

    const filteredDrafts = useMemo(() => {
        return drafts.filter(draft => {
            const status = getRowStatus(draft);
            switch (filter) {
                case 'READY': return status === 'READY' || (status === 'NEEDS_CUSTOMER' && !!draft.clientName);
                case 'NEEDS_REVIEW': return status === 'LOW_CONFIDENCE' || status === 'NEEDS_PO' || status === 'NEEDS_CUSTOMER';
                case 'MISSING_REQUIRED': return status === 'NEEDS_PO' || status === 'MISSING_REQUIRED';
                case 'DUPLICATES': return status === 'DUPLICATE';
                case 'ALL':
                default:
                    return true;
            }
        });
    }, [drafts, filter]);

    // ─── Stats ───────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const total = drafts.length;
        const duplicates = drafts.filter(d => d._isDuplicate).length;
        const warnings = drafts.filter(d => d._warnings.length > 0).length;
        const selected = selectedRows.size;
        return { total, duplicates, warnings, selected };
    }, [drafts, selectedRows]);

    // ─── Selection Helpers ───────────────────────────────────────────
    const isRowValid = useCallback((d: DraftPO) => {
        const status = getRowStatus(d);
        return status === 'READY' || status === 'LOW_CONFIDENCE' || (status === 'NEEDS_CUSTOMER' && !!d.clientName);
    }, []);

    const toggleRow = useCallback((idx: number) => {
        const next = new Set(selectedRows);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        onSelectionChange(next);
    }, [selectedRows, onSelectionChange]);

    const toggleAll = useCallback(() => {
        const validIndices = filteredDrafts.filter(isRowValid).map(d => d._rowIndex);
        const allValidSelected = validIndices.length > 0 && validIndices.every(idx => selectedRows.has(idx));
        
        if (allValidSelected) {
            const next = new Set(selectedRows);
            validIndices.forEach(idx => next.delete(idx));
            onSelectionChange(next);
        } else {
            const next = new Set(selectedRows);
            validIndices.forEach(idx => next.add(idx));
            onSelectionChange(next);
        }
    }, [filteredDrafts, selectedRows, onSelectionChange, isRowValid]);

    const allSelected = filteredDrafts.length > 0 && filteredDrafts.filter(isRowValid).every(d => selectedRows.has(d._rowIndex)) && filteredDrafts.filter(isRowValid).length > 0;

    const selectValid = useCallback(() => {
        const valid = new Set(filteredDrafts.filter(isRowValid).map(d => d._rowIndex));
        onSelectionChange(valid);
    }, [filteredDrafts, onSelectionChange, isRowValid]);

    const deselectDuplicates = useCallback(() => {
        const next = new Set(selectedRows);
        drafts.forEach(d => {
            if (d._isDuplicate) next.delete(d._rowIndex);
        });
        onSelectionChange(next);
    }, [drafts, selectedRows, onSelectionChange]);

    // ─── Update Draft Helper ─────────────────────────────────────────
    const updateDraft = useCallback((rowIndex: number, field: keyof DraftPO, value: unknown) => {
        const updated = drafts.map(d => {
            if (d._rowIndex !== rowIndex) return d;
            
            const newDraft = { ...d, [field]: value };
            // If user manually edits clientName, clear out suggestions, alias state, and customerId to force re-resolution
            if (field === 'clientName' && value !== d.clientName) {
                newDraft.customerId = null;
                newDraft._customerSuggestions = undefined;
                newDraft._originalClientName = undefined;
                newDraft._saveAsAlias = false;
            }
            return newDraft;
        });
        onDraftsChange(updated);
    }, [drafts, onDraftsChange]);

    const selectCustomerSuggestion = useCallback((rowIndex: number, suggestion: CustomerSuggestion) => {
        const updated = drafts.map(d => {
            if (d._rowIndex !== rowIndex) return d;
            return {
                ...d,
                customerId: suggestion.customerId,
                _originalClientName: d.clientName || undefined, // Preserve before overwrite
                clientName: suggestion.name,
                _saveAsAlias: false, // Unchecked by default
                _customerSuggestions: undefined, // Clear suggestions
                _warnings: d._warnings.filter(w => !w.includes('not found')) // Clear "not found" warning
            };
        });
        onDraftsChange(updated);
    }, [drafts, onDraftsChange]);

    // ─── Row Class ───────────────────────────────────────────────────
    const getRowClasses = (draft: DraftPO): string => {
        const base = 'border-b border-slate-100 transition-colors hover:bg-blue-50/50';
        if (draft._isDuplicate) return `${base} border-l-4 border-l-orange-400 bg-orange-50/30`;
        if (!draft.poNumber || !draft.clientName) return `${base} border-l-4 border-l-red-400`;
        return base;
    };

    // ─── Type / Priority Options ─────────────────────────────────────
    const typeOptions = [
        { value: 'inbound', label: 'Inbound' },
        { value: 'outbound', label: 'Outbound' },
    ];
    const priorityOptions = [
        { value: 'standard', label: 'Standard' },
        { value: 'urgent', label: 'Urgent' },
        { value: 'critical', label: 'Critical' },
    ];

    // ─── Render ──────────────────────────────────────────────────────
    return (
        <div className="space-y-3">
            {/* ── Controls Bar ───────────────────────────────────── */}
            <div className="bg-white/70 border border-slate-200 rounded-xl px-4 py-2.5 flex flex-wrap items-center gap-3">
                {/* Summary */}
                <p className="text-sm text-slate-600 mr-auto flex items-center gap-3">
                    <span>
                        <span className="font-semibold tabular-nums text-slate-900">
                            {stats.selected}
                        </span>
                        {' '}of{' '}
                        <span className="tabular-nums">{stats.total}</span>
                        {' '}rows
                        {stats.duplicates > 0 && (
                            <span className="text-orange-600 ml-2">
                                · {stats.duplicates} duplicate{stats.duplicates !== 1 ? 's' : ''}
                            </span>
                        )}
                        {stats.warnings > 0 && (
                            <span className="text-amber-600 ml-2">
                                · {stats.warnings} warning{stats.warnings !== 1 ? 's' : ''}
                            </span>
                        )}
                    </span>
                    
                    {/* Filters */}
                    <span className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg ml-4">
                        {(['ALL', 'READY', 'NEEDS_REVIEW', 'MISSING_REQUIRED', 'DUPLICATES'] as FilterState[]).map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                                    filter === f 
                                        ? 'bg-white text-blue-700 shadow-sm ring-1 ring-slate-200' 
                                        : 'text-slate-600 hover:bg-slate-200/50 hover:text-slate-900'
                                }`}
                            >
                                {f.replace('_', ' ')}
                            </button>
                        ))}
                    </span>
                </p>

                {/* Action Buttons */}
                <button
                    onClick={selectValid}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                >
                    <CheckCircle2 size={13} />
                    Select Valid
                </button>
                {stats.duplicates > 0 && (
                    <button
                        onClick={deselectDuplicates}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                    >
                        <Copy size={13} />
                        Deselect Duplicates
                    </button>
                )}
                <button
                    onClick={toggleAll}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
                >
                    <ArrowUpDown size={13} />
                    {allSelected ? 'Deselect All' : 'Select All'}
                </button>
            </div>

            {/* ── Table Container ────────────────────────────────── */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        {/* ── Header ─────────────────────────────── */}
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-3 py-2.5 w-10">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleAll}
                                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                    />
                                </th>
                                {[
                                    { label: '#', width: 'w-12' },
                                    { label: 'PO Number', width: 'min-w-[140px]' },
                                    { label: 'Client', width: 'min-w-[160px]' },
                                    { label: 'Type', width: 'w-28' },
                                    { label: 'Due Date', width: 'min-w-[130px]' },
                                    { label: 'Priority', width: 'w-28' },
                                    { label: 'Skids', width: 'w-20' },
                                    { label: 'Weight', width: 'w-24' },
                                    { label: 'Confidence', width: 'w-28' },
                                    { label: 'Status', width: 'min-w-[120px]' },
                                ].map(col => (
                                    <th
                                        key={col.label}
                                        className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 ${col.width}`}
                                    >
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {/* ── Body ───────────────────────────────── */}
                        <tbody>
                            {filteredDrafts.map(draft => {
                                const isSelected = selectedRows.has(draft._rowIndex);
                                const fc = draft._fieldConfidence;

                                return (
                                    <>
                                    <tr
                                        key={draft._rowIndex}
                                        className={getRowClasses(draft)}
                                    >
                                        {/* Checkbox */}
                                        <td className="px-3 py-2">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                disabled={!isRowValid(draft)}
                                                onChange={() => toggleRow(draft._rowIndex)}
                                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            />
                                        </td>

                                        {/* Row Index */}
                                        <td className="px-3 py-2 text-xs text-slate-400 tabular-nums font-mono">
                                            {draft._rowIndex}
                                        </td>

                                        {/* PO Number */}
                                        <td className="px-1 py-1">
                                            <EditableCell
                                                value={draft.poNumber ?? ''}
                                                onChange={val => updateDraft(draft._rowIndex, 'poNumber', val || null)}
                                                isRequired
                                                isMissing={!draft.poNumber}
                                                confidence={fc['poNumber']}
                                                mono
                                                placeholder="PO-XXXX"
                                            />
                                        </td>

                                        {/* Client */}
                                        <td className="px-1 py-1 align-top pt-2 relative">
                                            <EditableCell
                                                value={draft.clientName ?? ''}
                                                onChange={val => updateDraft(draft._rowIndex, 'clientName', val || null)}
                                                isRequired
                                                isMissing={!draft.clientName}
                                                confidence={fc['clientName']}
                                                placeholder="Client name"
                                            />
                                            {draft._customerSuggestions && draft._customerSuggestions.length > 0 && (
                                                <div className="mt-1.5 p-1.5 bg-blue-50/50 border border-blue-100 rounded text-xs">
                                                    <p className="text-[10px] uppercase font-bold text-blue-600 tracking-wider mb-1 px-1">
                                                        Did you mean?
                                                    </p>
                                                    <div className="space-y-1">
                                                        {draft._customerSuggestions.map((sug) => {
                                                            const color = sug.confidence >= 95 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                                                                          sug.confidence >= 85 ? 'text-blue-700 bg-blue-50 border-blue-200' :
                                                                          'text-amber-700 bg-amber-50 border-amber-200';
                                                            return (
                                                                <button
                                                                    key={sug.customerId}
                                                                    onClick={() => selectCustomerSuggestion(draft._rowIndex, sug)}
                                                                    className={`w-full text-left flex items-center justify-between p-1.5 rounded border transition-colors hover:bg-white shadow-sm ${color}`}
                                                                >
                                                                    <div className="flex flex-col gap-0.5 min-w-0">
                                                                        <span className="font-semibold truncate">{sug.name}</span>
                                                                        <span className="text-[9px] opacity-80">{sug.reason}</span>
                                                                    </div>
                                                                    <div className="flex flex-col items-end gap-1 ml-2 shrink-0">
                                                                        <span className="text-[10px] font-bold opacity-90">{sug.confidence}%</span>
                                                                        <span className="text-[9px] underline opacity-70">Select</span>
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            {/* V1.4C Alias Learning Checkbox */}
                                            {draft._originalClientName && draft.clientName && draft._originalClientName.toLowerCase().trim() !== draft.clientName.toLowerCase().trim() && (
                                                <div className="mt-2 flex items-start gap-2 bg-blue-50/50 p-2 rounded border border-blue-100/50 shadow-sm">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={draft._saveAsAlias || false}
                                                        onChange={(e) => updateDraft(draft._rowIndex, '_saveAsAlias', e.target.checked)}
                                                        className="mt-0.5 w-3.5 h-3.5 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer shrink-0"
                                                        id={`alias-${draft._rowIndex}`}
                                                    />
                                                    <label htmlFor={`alias-${draft._rowIndex}`} className="text-xs text-slate-600 leading-tight cursor-pointer select-none">
                                                        Save "<span className="font-semibold text-slate-800">{draft._originalClientName}</span>" as an alias for {draft.clientName}
                                                    </label>
                                                </div>
                                            )}
                                        </td>

                                        {/* Type */}
                                        <td className="px-3 py-2">
                                            <DropdownCell
                                                value={draft.type}
                                                options={typeOptions}
                                                onChange={val => updateDraft(draft._rowIndex, 'type', val)}
                                                confidence={fc['type']}
                                            />
                                        </td>

                                        {/* Due Date */}
                                        <td className="px-1 py-1">
                                            <EditableCell
                                                value={draft.dueDate ?? ''}
                                                onChange={val => updateDraft(draft._rowIndex, 'dueDate', val || null)}
                                                confidence={fc['dueDate']}
                                                type="date"
                                                placeholder="YYYY-MM-DD"
                                            />
                                        </td>

                                        {/* Priority */}
                                        <td className="px-3 py-2">
                                            <DropdownCell
                                                value={draft.priority ?? 'standard'}
                                                options={priorityOptions}
                                                onChange={val => updateDraft(draft._rowIndex, 'priority', val as DraftPO['priority'])}
                                                confidence={fc['priority']}
                                            />
                                        </td>

                                        {/* Skids */}
                                        <td className="px-1 py-1">
                                            <EditableCell
                                                value={draft.totalSkids != null ? String(draft.totalSkids) : ''}
                                                onChange={val => updateDraft(draft._rowIndex, 'totalSkids', val ? Number(val) : null)}
                                                confidence={fc['totalSkids']}
                                                type="number"
                                                placeholder="N/A"
                                            />
                                        </td>

                                        {/* Weight */}
                                        <td className="px-1 py-1">
                                            <EditableCell
                                                value={draft.totalWeight != null ? String(draft.totalWeight) : ''}
                                                onChange={val => updateDraft(draft._rowIndex, 'totalWeight', val ? Number(val) : null)}
                                                confidence={fc['totalWeight']}
                                                type="number"
                                                placeholder="N/A"
                                            />
                                        </td>

                                        {/* Confidence */}
                                        <td className="px-3 py-2">
                                            <ConfidenceDot confidence={draft._confidence} />
                                        </td>

                                        {/* Status */}
                                        <td className="px-3 py-2">
                                            <StatusBadges draft={draft} />
                                        </td>
                                    </tr>
                                    {(draft._warnings.length > 0 || draft._isVisualFallback) && (
                                        <tr key={`warn-${draft._rowIndex}`} className="bg-amber-50/30">
                                            <td colSpan={10} className="px-3 py-2 border-b border-slate-100">
                                                <div className="flex flex-col gap-1.5">
                                                    {draft._isVisualFallback && (
                                                        <div className="flex items-start gap-1.5 text-xs text-purple-800 font-medium bg-purple-100/50 p-1.5 rounded">
                                                            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-purple-600" />
                                                            <span>Extracted visually from scanned PDF. Verify fields before creating.</span>
                                                        </div>
                                                    )}
                                                    
                                                    {draft._warnings.map((w, i) => {
                                                        const isMissing = w.toLowerCase().includes('missing') || w.toLowerCase().includes('not found');
                                                        const isCustomer = w.toLowerCase().includes('client');
                                                        
                                                        const Icon = isMissing ? AlertTriangle : AlertTriangle;
                                                        const colorClass = isMissing ? 'text-red-700' : isCustomer ? 'text-orange-700' : 'text-amber-800';
                                                        const iconColor = isMissing ? 'text-red-500' : isCustomer ? 'text-orange-500' : 'text-amber-500';

                                                        return (
                                                            <div key={i} className={`flex items-start gap-1.5 text-xs ${colorClass}`}>
                                                                <Icon size={14} className={`mt-0.5 shrink-0 ${iconColor}`} />
                                                                <span>{w}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Empty State */}
                    {drafts.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                            <ArrowUpDown size={32} className="mb-2 text-slate-300" />
                            <p className="text-sm font-medium text-slate-500">No draft POs to review</p>
                            <p className="text-xs text-slate-400 mt-1">Upload a file to extract purchase orders</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default POImportReviewTable;
